import { get, post, del } from '../httpMethods';
import { ApiResponse, PaginationMeta } from '../../types/api';
import type { PurgePreview, PurgeResult, RetentionUnit } from '../../types/retention';

export type PharmacyStockTransactionType =
  | 'RECEIPT'
  | 'ISSUE'
  | 'RETURN'
  | 'ADJUSTMENT'
  | 'EXPIRY_WRITE_OFF';

export interface PharmacyStockLevel {
  drugId: string;
  drugCode: string;
  drugName: string;
  form?: string;
  strength?: string;
  unitOfMeasure: string;
  category?: string;
  company?: string;
  price: number;
  currentStock: number;
  reorderLevel: number;
  reorderQuantity: number;
  isLowStock: boolean;
}

export interface PharmacyStockBatch {
  id: string;
  drugId: string;
  drug?: { id: string; name: string; drugCode: string };
  supplier?: { id: string; name: string };
  batchNumber: string;
  expiryDate?: string;
  quantityReceived: number;
  quantityRemaining: number;
  unitCost?: number;
  daysToExpiry?: number | null;
  isExpired?: boolean;
  isExpiringSoon?: boolean;
  createdAt: string;
}

export interface PharmacyStockLedgerEntry {
  id: string;
  drugId: string;
  drug?: { id: string; name: string; drugCode: string };
  batchId?: string;
  type: PharmacyStockTransactionType;
  quantity: number;
  unitCost?: number;
  referenceType?: string;
  referenceId?: string;
  notes?: string;
  occurredAt: string;
  createdAt: string;
}

export interface PharmacyStockFilters {
  drugId?: string;
  search?: string;
  lowOnly?: boolean;
}

export const pharmacyStockService = {
  getStock: (
    page = 1,
    limit = 20,
    filters?: PharmacyStockFilters,
  ): Promise<ApiResponse<PharmacyStockLevel[]> & { meta: PaginationMeta }> => {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (filters?.drugId) params.append('drugId', filters.drugId);
    if (filters?.search) params.append('search', filters.search);
    if (filters?.lowOnly) params.append('lowOnly', 'true');
    return get<PharmacyStockLevel[]>(`/pharmacy/stock?${params.toString()}`) as Promise<
      ApiResponse<PharmacyStockLevel[]> & { meta: PaginationMeta }
    >;
  },
  getBatches: (drugId?: string, expiringInDays?: number): Promise<ApiResponse<PharmacyStockBatch[]>> => {
    const params = new URLSearchParams();
    if (drugId) params.append('drugId', drugId);
    if (expiringInDays !== undefined) params.append('expiringInDays', String(expiringInDays));
    const query = params.toString() ? `?${params.toString()}` : '';
    return get<PharmacyStockBatch[]>(`/pharmacy/stock/batches${query}`);
  },
  getExpiring: (days = 30): Promise<ApiResponse<PharmacyStockBatch[]>> =>
    get<PharmacyStockBatch[]>(`/pharmacy/expiring?days=${days}`),
  getLedger: (
    page = 1,
    limit = 20,
    filters?: {
      drugId?: string;
      type?: string;
      from?: string;
      to?: string;
      search?: string;
    },
  ): Promise<ApiResponse<PharmacyStockLedgerEntry[]> & { meta: PaginationMeta }> => {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (filters?.drugId) params.append('drugId', filters.drugId);
    if (filters?.type) params.append('type', filters.type);
    if (filters?.from) params.append('from', filters.from);
    if (filters?.to) params.append('to', filters.to);
    if (filters?.search) params.append('search', filters.search);
    return get<PharmacyStockLedgerEntry[]>(`/pharmacy/ledger?${params.toString()}`) as Promise<
      ApiResponse<PharmacyStockLedgerEntry[]> & { meta: PaginationMeta }
    >;
  },
  deleteLedgerEntry: (id: string): Promise<ApiResponse<{ deleted: boolean }>> =>
    del<{ deleted: boolean }>(`/pharmacy/ledger/${id}`),
  bulkDeleteLedger: (
    ids: string[],
  ): Promise<ApiResponse<{ deleted: string[]; notFound: string[] }>> =>
    post<{ deleted: string[]; notFound: string[] }>('/pharmacy/ledger/bulk-delete', { ids }),
  /** How many ledger entries a purge with the given retention window would remove. */
  previewPurgeLedger: (value: number, unit: RetentionUnit): Promise<ApiResponse<PurgePreview>> =>
    get<PurgePreview>('/pharmacy/ledger/purge/preview', { value, unit }),
  /** Permanently delete ledger entries older than the retention window. */
  purgeLedger: (value: number, unit: RetentionUnit): Promise<ApiResponse<PurgeResult>> =>
    post<PurgeResult>('/pharmacy/ledger/purge', { value, unit }),
  adjustStock: (data: {
    drugId: string;
    batchId?: string;
    quantity: number;
    type?: PharmacyStockTransactionType;
    unitCost?: number;
    reason?: string;
  }): Promise<ApiResponse<PharmacyStockLedgerEntry>> => post<PharmacyStockLedgerEntry>('/pharmacy/stock/adjust', data),
  /** Set a drug's stock to an absolute level (reconciles batches + ledger). */
  setStock: (data: {
    drugId: string;
    quantity: number;
    reason?: string;
  }): Promise<ApiResponse<PharmacyStockLedgerEntry | null>> =>
    post<PharmacyStockLedgerEntry | null>('/pharmacy/stock/set', data),
  issueStock: (data: {
    drugId: string;
    quantity: number;
    prescriptionId?: string;
    patientId?: string;
    visitId?: string;
    notes?: string;
  }): Promise<ApiResponse<PharmacyStockLedgerEntry[]>> => post<PharmacyStockLedgerEntry[]>('/pharmacy/stock/issue', data),
};
